import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import CommonService from "../../services/common.service";
import { API_URLS } from "../../constants/api-urls";
import "./ReviewBids.scss";

export default function ReviewBids() {
  const { auctionId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filterType = searchParams.get('filter'); // 'rejected' or null for active bids
  const [auction, setAuction] = useState(null);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isEditingReservePrice, setIsEditingReservePrice] = useState(false);
  const [newReservePrice, setNewReservePrice] = useState("");
  const [selectedExtensionDays, setSelectedExtensionDays] = useState(14);
  const [showExtensionConfirmModal, setShowExtensionConfirmModal] = useState(false);
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
  const [declaringWinner, setDeclaringWinner] = useState(false);

  useEffect(() => {
    fetchAuctionAndBids();
  }, [auctionId, filterType]);

  useEffect(() => {
    if (
      !auction ||
      (auction.winner_approval_status !== "pending" &&
        auction.status !== "pending_winner_approval")
    ) {
      return undefined;
    }
    const interval = setInterval(() => {
      fetchAuctionAndBids();
    }, 15000);
    return () => clearInterval(interval);
  }, [auction?.winner_approval_status, auction?.status, auctionId]);

  const fetchAuctionAndBids = async () => {
    try {
      setLoading(true);
      
      // Fetch auction details
      const auctionResponse = await CommonService.get(`${API_URLS.AUCTION.BASE}/${auctionId}`);
      if (auctionResponse && auctionResponse.success) {
        setAuction(auctionResponse.data);
      }

      // Fetch bids for this auction
      const bidsResponse = await CommonService.get(API_URLS.AUCTION.BIDS(auctionId));
      if (bidsResponse && bidsResponse.success && bidsResponse.data) {
        let filteredBids = Array.isArray(bidsResponse.data) ? bidsResponse.data : [];
        
        // Filter based on filterType
        if (filterType === 'rejected') {
          // Show only rejected bids
          filteredBids = filteredBids.filter(bid => bid.bid_status === 'rejected');
        } else {
          // Show only active/outbid bids (default behavior)
          filteredBids = filteredBids.filter(bid => bid.bid_status !== 'rejected');
        }
        
        // Sort by amount descending (highest first)
        const sortedBids = filteredBids.sort((a, b) => {
          const amountA = parseFloat(a.bid_amount || a.amount || 0);
          const amountB = parseFloat(b.bid_amount || b.amount || 0);
          return amountB - amountA; // Descending order
        });
        
        console.log(`Filtered bids (${filterType || 'active'}):`, sortedBids);
        setBids(sortedBids);
      }
    } catch (error) {
      console.error("Error fetching auction and bids:", error);
      toast.error("Failed to load auction details");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || !isFinite(numAmount)) {
      return "₹0";
    }
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(numAmount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString("en-IN", {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
        hour12: true,
      });
    } catch (error) {
      return dateString;
    }
  };

  const handleAcceptBid = async (bidId) => {
    if (!window.confirm("Are you sure you want to accept this bid?")) {
      return;
    }

    try {
      const response = await CommonService.post(API_URLS.AUCTION.ACCEPT_BID(auctionId), {
        bidId: bidId,
      });
      
      if (response && response.success) {
        toast.success("Bid accepted successfully!");
        // Redirect to Proforma Invoice page
        navigate(`/auction/${auctionId}/proforma-invoice`);
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || "Failed to accept bid";
      toast.error(errorMessage);
    }
  };

  const handleEndAuctionClick = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmEndAuction = async () => {
    setDeclaringWinner(true);
    try {
      const response = await CommonService.post(API_URLS.AUCTION.END(auctionId), {});
      if (response?.success) {
        setShowConfirmModal(false);
        const data = response.data || {};
        if (data.winner_declared) {
          toast.success(data.message || "Winner declared successfully.");
          navigate(`/auction/${auctionId}/winning-bid-review`);
        } else if (data.requires_winner_approval) {
          toast.info(
            data.message ||
              "Winner declaration submitted for approval. Confirm & Declare Winner will be available once approvers complete the review.",
          );
          await fetchAuctionAndBids();
        } else {
          toast.success(data.message || "Winner declared successfully.");
          navigate(`/auction/${auctionId}/winning-bid-review`);
        }
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to declare winner";
      toast.error(errorMessage);
    } finally {
      setDeclaringWinner(false);
    }
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-IN", {
        year: "numeric",
        month: "numeric",
        day: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  const handleEditReservePrice = () => {
    const currentReserve = parseFloat(auction.reserve_price || 0);
    setNewReservePrice(currentReserve.toString());
    setIsEditingReservePrice(true);
  };

  const handleCancelReservePriceEdit = () => {
    setIsEditingReservePrice(false);
    setNewReservePrice("");
  };

  const handleSaveReservePrice = async () => {
    const priceValue = parseFloat(newReservePrice);
    if (isNaN(priceValue) || priceValue <= 0) {
      toast.error("Please enter a valid reserve price");
      return;
    }

    try {
      // Use the update reserve price endpoint
      const response = await CommonService.put(`${API_URLS.AUCTION.BASE}/${auctionId}/reserve-price`, {
        reserve_price: priceValue,
      });

      if (response && response.success) {
        toast.success("Reserve price updated successfully!");
        setIsEditingReservePrice(false);
        setNewReservePrice("");
        fetchAuctionAndBids(); // Refresh data
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.response?.data?.error || "Failed to update reserve price";
      toast.error(errorMessage);
      console.error("Error updating reserve price:", error);
    }
  };

  const handleCancelAuction = async () => {
    try {
      const response = await CommonService.post(API_URLS.AUCTION.CANCEL(auctionId));

      if (response && response.success) {
        toast.success("Auction cancelled successfully!");
        setShowCancelConfirmModal(false);
        // Redirect to My Auctions page
        navigate('/auction/my/auctions');
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.response?.data?.error || "Failed to cancel auction";
      toast.error(errorMessage);
      console.error("Error cancelling auction:", error);
    }
  };

  const handleConfirmExtension = async () => {
    try {
      // Prepare request data
      const requestData = {
        extension_days: selectedExtensionDays,
      };

      // Include new reserve price if user has entered one (either editing or has a value)
      if (newReservePrice && newReservePrice.trim() !== "") {
        const priceValue = parseFloat(newReservePrice);
        if (!isNaN(priceValue) && priceValue > 0) {
          requestData.new_reserve_price = priceValue;
        }
      }

      const response = await CommonService.post(`${API_URLS.AUCTION.BASE}/${auctionId}/extend`, requestData);

      if (response && response.success) {
        toast.success("Auction extension confirmed successfully!");
        setShowExtensionConfirmModal(false);
        setIsEditingReservePrice(false);
        setNewReservePrice("");
        fetchAuctionAndBids(); // Refresh data
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.response?.data?.error || "Failed to extend auction";
      toast.error(errorMessage);
      console.error("Error extending auction:", error);
    }
  };

  if (loading) {
    return <div className="review-bids-page loading">Loading bids...</div>;
  }

  if (!auction) {
    return <div className="review-bids-page error">Auction not found</div>;
  }

  // Check if reserve price is reached and auction hasn't ended
  const reservePriceReached = auction.reserve_price_reached === 'Y' || auction.reserve_price_reached === 'y';
  const auctionEndDate = auction.end_date ? new Date(auction.end_date) : null;
  const auctionStatus = String(auction.status || '').toLowerCase();
  const isBiddingPeriodOpen =
    Boolean(auctionEndDate && auctionEndDate > new Date()) &&
    ['active', 'published', 'extended'].includes(auctionStatus);

  // Show all bids for this auction (sorted by amount descending)
  const allBids = bids;
  const highestBid = allBids.length > 0 ? allBids[0] : null;
  const highestBidAmount = highestBid ? parseFloat(highestBid.bid_amount || highestBid.amount || 0) : 0;
  
  // Calculate shortfall and reserve gap for reserve not met scenario
  const reservePrice = parseFloat(auction.reserve_price || 0);
  const shortfall = reservePrice > highestBidAmount ? reservePrice - highestBidAmount : 0;
  // Reserve gap as positive percentage showing how much below reserve price the highest bid is
  const reserveGap = reservePrice > 0 && shortfall > 0 ? ((shortfall / reservePrice) * 100).toFixed(1) : 0;
  
  // Check if a bid has been accepted
  const acceptedBid = auction?.seller_decision === 'accepted' && auction?.winning_bid_id
    ? allBids.find(bid => bid.bid_id === auction.winning_bid_id)
    : null;
  
  const winnerApprovalPending =
    auction.winner_approval_status === "pending" ||
    auction.status === "pending_winner_approval";
  const winnerApprovalRejected = auction.winner_approval_status === "rejected";
  const winnerFullyDeclared =
    auctionStatus === "ended" && Boolean(auction.winning_bid_id);
  const winnerNeedsFinalDeclaration =
    auction.winner_approval_status === "approved" &&
    auction.status !== "ended" &&
    auction.status !== "pending_winner_approval";
  const winnerApprovalApprovedReady = winnerNeedsFinalDeclaration;

  // Only show reserve reached design while bidding is still open and winner not yet declared
  const showReserveReachedDesign =
    reservePriceReached && isBiddingPeriodOpen && !acceptedBid && !winnerFullyDeclared;

  // Reserve Price Reached: show "Continue Auction" only for End on Scheduled Date Only and until end date
  const isEndOnScheduledOnly = auction.auction_end_condition === 'End on Scheduled Date Only';
  const isEndWhenReserveMet = auction.auction_end_condition === 'End When Reserve Met';
  const showContinueAuctionButton = showReserveReachedDesign && isEndOnScheduledOnly && isBiddingPeriodOpen;
  
  // Show auction ended design when reserve price is reached, bidding period closed, and no bid accepted
  const showAuctionEndedDesign =
    reservePriceReached && !isBiddingPeriodOpen && !acceptedBid && !winnerFullyDeclared;
  
  // Show reserve not met design when reserve price is NOT reached, auction has ended, and no bid accepted
  const showReserveNotMetDesign = !reservePriceReached && !isBiddingPeriodOpen && !acceptedBid;
  
  // Show default bids section when reserve price is NOT reached, auction is active, and no bid accepted
  const showDefaultBidsSection =
    !reservePriceReached && isBiddingPeriodOpen && !acceptedBid;

  const showWinnerDeclaredPendingAcceptance =
    winnerFullyDeclared && !acceptedBid;

  const canShowDeclareWinnerButton =
    !winnerApprovalPending &&
    !acceptedBid &&
    !winnerFullyDeclared &&
    (allBids.length > 0 || winnerNeedsFinalDeclaration);
  const declareWinnerButtonLabel = winnerNeedsFinalDeclaration
    ? "Confirm & Declare Winner"
    : isEndWhenReserveMet && showReserveReachedDesign
      ? "End Auction Now & Accept Highest Bid"
      : "End Auction & Declare Winner";

  return (
    <div className="review-bids-page">
      <div className="container">
        {/* Back Button */}
        <button className="back-btn" onClick={() => navigate(-1)}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m12 19-7-7 7-7"></path>
            <path d="M19 12H5"></path>
          </svg>
          Back
        </button>

        {winnerApprovalPending && (
          <div
            className="winner-approval-banner"
            style={{
              marginBottom: "16px",
              padding: "14px 16px",
              borderRadius: "8px",
              background: "#EFF6FF",
              border: "1px solid #BFDBFE",
              color: "#1D4ED8",
              fontWeight: 600,
            }}
          >
            Pending Winner Approval — winner declaration is with configured approvers. Confirm &
            Declare Winner will be available once approval is complete.
          </div>
        )}
        {winnerApprovalApprovedReady && (
          <div
            className="winner-approval-banner approved"
            style={{
              marginBottom: "16px",
              padding: "14px 16px",
              borderRadius: "8px",
              background: "#ECFDF5",
              border: "1px solid #A7F3D0",
              color: "#047857",
              fontWeight: 600,
            }}
          >
            Winner approval complete — click Confirm &amp; Declare Winner to officially declare the
            highest valid bidder.
          </div>
        )}
        {winnerApprovalRejected && (
          <div
            className="winner-approval-banner rejected"
            style={{
              marginBottom: "16px",
              padding: "14px 16px",
              borderRadius: "8px",
              background: "#FEF2F2",
              border: "1px solid #FECACA",
              color: "#B91C1C",
              fontWeight: 600,
            }}
          >
            Winner declaration was rejected by approvers. You can submit again using End Auction
            &amp; Declare Winner.
          </div>
        )}

        {/* Rejected Bids Only Section - Show when filter=rejected */}
        {filterType === 'rejected' ? (
          <>
            {/* Header */}
            <div className="page-header">
              <div className="header-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              </div>
              <div className="header-content">
                <h1>Rejected Bids</h1>
                <p>Bids that have been rejected by the seller</p>
              </div>
            </div>

            {/* Rejected Bids List */}
            <div className="bids-section">
              <h2 className="section-title">Rejected Bids ({allBids.length})</h2>
              
              {allBids.length === 0 ? (
                <div className="no-bids">
                  <p>No rejected bids for this auction</p>
                </div>
              ) : (
                <div className="bids-list">
                  {allBids.map((bid) => {
                    return (
                      <div
                        key={bid.bid_id}
                        className="bid-card rejected-bid"
                      >
                        <div className="bid-left">
                          <div className="bidder-name">{bid.bidder_name || bid.bidder_company || "Unknown Bidder"}</div>
                          <div className="bid-time">{formatDate(bid.bid_placed_at || bid.created_at || bid.bid_date)}</div>
                        </div>
                        <div className="bid-right">
                          <div className="bid-amount">{formatCurrency(bid.bid_amount || bid.amount)}</div>
                          <div className="rejected-bid-label">Rejected</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            {/* Accepted Bid Section - Show when bid is accepted */}
            {acceptedBid && (
              <div className="accepted-bid-section">
                <div className="accepted-bid-header">
                  <div className="accepted-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                  </div>
                  <h2>Accepted Bid</h2>
                </div>
                <div className="accepted-bid-card">
                  <div className="bid-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                  </div>
                  <div className="bid-left">
                    <div className="bidder-name-row">
                      <div className="bidder-name">{acceptedBid.bidder_name || acceptedBid.bidder_company || "Unknown Bidder"}</div>
                      <div className="accepted-bid-label">Accepted</div>
                    </div>
                    <div className="bid-time">{formatDate(acceptedBid.bid_placed_at || acceptedBid.created_at || acceptedBid.bid_date)}</div>
                  </div>
                  <div className="bid-right">
                    <div className="bid-amount">{formatCurrency(acceptedBid.bid_amount || acceptedBid.amount)}</div>
                  </div>
                </div>
              </div>
            )}

            {showReserveReachedDesign ? (
          <>
            {/* Reserve Price Reached Banner */}
            <div className="reserve-reached-banner">
              <div className="banner-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                </svg>
              </div>
              <div className="banner-content">
                <h1>Reserve Price Reached!</h1>
                <p>Your auction has reached the reserve price. You can now end the auction or let it continue.</p>
              </div>
            </div>

            {/* Auction Details Section */}
            <div className="auction-details-section">
              <h2 className="section-title">Auction Details</h2>
              <div className="details-card">
                <div className="details-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                    <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                    <line x1="12" y1="22.08" x2="12" y2="12"></line>
                  </svg>
                </div>
                <div className="details-content">
                  <h3>{auction.product_name}</h3>
                  <p className="product-description">{auction.product_description || "No description available."}</p>
                  <div className="details-grid">
                    <div className="detail-item">
                      <span className="label">Category:</span>
                      <span className="value">{auction.category_name || "N/A"}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Reserve Price:</span>
                      <span className="value">{formatCurrency(auction.reserve_price || 0)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Start Date:</span>
                      <span className="value">{formatShortDate(auction.start_date)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">End Date:</span>
                      <span className="value">{formatShortDate(auction.end_date)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bidding Statistics Section */}
            <div className="bidding-statistics-section">
              <h2 className="section-title">Bidding Statistics</h2>
              <div className="statistics-grid">
                <div className="stat-card stat-total-bids">
                  <div className="stat-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                  </div>
                  <div className="stat-value">{allBids.length}</div>
                  <div className="stat-label">Total Bids</div>
                </div>
                <div className="stat-card stat-highest-bid">
                  <div className="stat-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                    </svg>
                  </div>
                  <div className="stat-value">{formatCurrency(highestBidAmount)}</div>
                  <div className="stat-label">Highest Bid</div>
                </div>
                <div className="stat-card stat-status">
                  <div className="stat-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                  </div>
                  <div className="stat-value">Reserve Met ✓</div>
                  <div className="stat-label">Status</div>
                </div>
              </div>
            </div>

            {/* Recent Bids Section */}
            <div className="bids-section">
              <h2 className="section-title">{filterType === 'rejected' ? 'Rejected Bids' : 'Recent Bids'}</h2>
              
              {allBids.length === 0 ? (
                <div className="no-bids">
                  <p>{filterType === 'rejected' ? 'No rejected bids for this auction' : 'No bids available for this auction'}</p>
                </div>
              ) : (
                <div className="bids-list">
                  {allBids.map((bid, index) => {
                    const isHighest = bid.is_highest_bid === 'Y' || bid.is_highest_bid === 'y' || index === 0;
                    const isAccepted = acceptedBid && bid.bid_id === acceptedBid.bid_id;
                    const isRejected = bid.bid_status === 'rejected' || filterType === 'rejected';
                    return (
                      <div
                        key={bid.bid_id}
                        className={`bid-card ${isHighest && !isAccepted && !isRejected ? 'highest-bid' : ''} ${isAccepted ? 'accepted-bid' : ''} ${isRejected ? 'rejected-bid' : ''}`}
                      >
                        <div className="bid-icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                            <circle cx="12" cy="7" r="4"></circle>
                          </svg>
                        </div>
                        <div className="bid-left">
                          <div className="bidder-name-row">
                            <div className="bidder-name">{bid.bidder_name || bid.bidder_company || "Unknown Bidder"}</div>
                            {isRejected && (
                              <div className="rejected-bid-label">Rejected</div>
                            )}
                            {!isRejected && isHighest && !isAccepted && (
                              <div className="highest-bid-label">Highest Bid</div>
                            )}
                            {!isRejected && isAccepted && (
                              <div className="accepted-bid-label">Accepted</div>
                            )}
                          </div>
                          <div className="bid-time">{formatDate(bid.bid_placed_at || bid.created_at || bid.bid_date)}</div>
                        </div>
                        <div className="bid-right">
                          <div className="bid-amount">{formatCurrency(bid.bid_amount || bid.amount)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Decision Section */}
            <div className="decision-section">
              <div className="decision-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                  <path d="M12 8v4"></path>
                  <path d="M12 16h.01"></path>
                </svg>
              </div>
              <div className="decision-content">
                <p>Your reserve price has been met. You can choose to end the auction now and accept the highest bid{showContinueAuctionButton ? ', or let the auction continue until the end date to potentially get higher bids' : ''}.</p>
                <div className="decision-buttons">
                  {showContinueAuctionButton && (
                    <button className="btn-continue" onClick={() => navigate(`/auction/${auctionId}`)}>
                      Continue Auction
                    </button>
                  )}
                  {canShowDeclareWinnerButton ? (
                    <button className="btn-end-auction" onClick={handleEndAuctionClick}>
                      {declareWinnerButtonLabel}
                    </button>
                  ) : winnerApprovalPending ? (
                    <button className="btn-end-auction" disabled>
                      Pending Winner Approval
                    </button>
                  ) : null}
                </div>
                {auction.end_date && isEndOnScheduledOnly && (
                  <p className="decision-note">
                    Note: Auction will automatically end on {formatShortDate(auction.end_date)} if not ended manually.
                  </p>
                )}
              </div>
            </div>
          </>
        ) : showAuctionEndedDesign ? (
          <>
            {/* Auction Time Ended Header */}
            <div className="auction-ended-header">
              <div className="header-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
              </div>
              <div className="header-content">
                <h1>Auction Time Ended!</h1>
                <p>The auction time period has ended. Please review bids and declare the winner.</p>
              </div>
            </div>

            {/* Auction Details Section */}
            <div className="auction-details-section">
              <h2 className="section-title">Auction Details</h2>
              <div className="details-card">
                <div className="details-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                    <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                    <line x1="12" y1="22.08" x2="12" y2="12"></line>
                  </svg>
                </div>
                <div className="details-content">
                  <h3>{auction.product_name}</h3>
                  <p className="product-description">{auction.product_description || "No description available."}</p>
                  <div className="details-grid">
                    <div className="detail-item">
                      <span className="label">Category:</span>
                      <span className="value">{auction.category_name || "N/A"}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Reserve Price:</span>
                      <span className="value">{formatCurrency(auction.reserve_price || 0)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Start Date:</span>
                      <span className="value">{formatShortDate(auction.start_date)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">End Date:</span>
                      <span className="value">{formatShortDate(auction.end_date)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bidding Statistics Section */}
            <div className="bidding-statistics-section">
              <h2 className="section-title">Bidding Statistics</h2>
              <div className="statistics-grid">
                <div className="stat-card stat-total-bids">
                  <div className="stat-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                  </div>
                  <div className="stat-value">{allBids.length}</div>
                  <div className="stat-label">Total Bids</div>
                </div>
                <div className="stat-card stat-highest-bid">
                  <div className="stat-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                    </svg>
                  </div>
                  <div className="stat-value">{formatCurrency(highestBidAmount)}</div>
                  <div className="stat-label">Highest Bid</div>
                </div>
                <div className="stat-card stat-status">
                  <div className="stat-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                  </div>
                  <div className="stat-value">Reserve Met ✓</div>
                  <div className="stat-label">Status</div>
                </div>
              </div>
            </div>

            {/* Recent Bids Section */}
            <div className="bids-section">
              <h2 className="section-title">{filterType === 'rejected' ? 'Rejected Bids' : 'Recent Bids'}</h2>
              
              {allBids.length === 0 ? (
                <div className="no-bids">
                  <p>{filterType === 'rejected' ? 'No rejected bids for this auction' : 'No bids available for this auction'}</p>
                </div>
              ) : (
                <div className="bids-list">
                  {allBids.map((bid, index) => {
                    const isHighest = bid.is_highest_bid === 'Y' || bid.is_highest_bid === 'y' || index === 0;
                    const isAccepted = acceptedBid && bid.bid_id === acceptedBid.bid_id;
                    const isRejected = bid.bid_status === 'rejected' || filterType === 'rejected';
                    return (
                      <div
                        key={bid.bid_id}
                        className={`bid-card ${isHighest && !isAccepted && !isRejected ? 'highest-bid' : ''} ${isAccepted ? 'accepted-bid' : ''} ${isRejected ? 'rejected-bid' : ''}`}
                      >
                        <div className="bid-icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                            <circle cx="12" cy="7" r="4"></circle>
                          </svg>
                        </div>
                        <div className="bid-left">
                          <div className="bidder-name-row">
                            <div className="bidder-name">{bid.bidder_name || bid.bidder_company || "Unknown Bidder"}</div>
                            {isRejected && (
                              <div className="rejected-bid-label">Rejected</div>
                            )}
                            {!isRejected && isHighest && !isAccepted && (
                              <div className="highest-bid-label">Highest Bid</div>
                            )}
                            {!isRejected && isAccepted && (
                              <div className="accepted-bid-label">Accepted</div>
                            )}
                          </div>
                          <div className="bid-time">{formatDate(bid.bid_placed_at || bid.created_at || bid.bid_date)}</div>
                        </div>
                        <div className="bid-right">
                          <div className="bid-amount">{formatCurrency(bid.bid_amount || bid.amount)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Important Decision Required Section */}
            <div className="important-decision-section">
              <div className="decision-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 9v4"></path>
                  <path d="M12 17h.01"></path>
                  <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"></path>
                </svg>
              </div>
              <div className="decision-content">
                <p>Important Decision Required: The auction time period has concluded and the reserve price has been met. Please declare the winner to proceed with the transaction.</p>
              </div>
            </div>

            {/* End Auction & Declare Winner Button */}
            <div className="declare-winner-section">
              {canShowDeclareWinnerButton ? (
                <button className="btn-declare-winner" onClick={handleEndAuctionClick}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
                    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path>
                    <path d="M4 22h16"></path>
                    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path>
                    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path>
                    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path>
                  </svg>
                  {declareWinnerButtonLabel}
                </button>
              ) : winnerApprovalPending ? (
                <button className="btn-declare-winner" disabled>
                  Pending Winner Approval
                </button>
              ) : null}
            </div>
          </>
        ) : showReserveNotMetDesign ? (
          <>
            {/* Auction Ended - Reserve Not Met Header */}
            <div className="reserve-not-met-header">
              <div className="header-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
              </div>
              <div className="header-content">
                <h1>Auction Ended - Reserve Not Met</h1>
                <p>Your auction has ended but the reserve price wasn't reached. We're offering you a special extension!</p>
              </div>
            </div>

            {/* Auction Summary Section */}
            <div className="auction-summary-section">
              <h2 className="section-title">Auction Summary</h2>
              <div className="summary-card">
                <h3>{auction.product_name}</h3>
                <p className="product-description">{auction.product_description || "No description available."}</p>
                <div className="summary-metrics">
                  <div className="metric-card metric-reserve">
                    <div className="metric-label">Reserve Price</div>
                    <div className="metric-value">{formatCurrency(reservePrice)}</div>
                  </div>
                  <div className="metric-card metric-highest">
                    <div className="metric-label">Highest Bid</div>
                    <div className="metric-value">{formatCurrency(highestBidAmount)}</div>
                  </div>
                  <div className="metric-card metric-shortfall">
                    <div className="metric-label">Shortfall</div>
                    <div className="metric-value">{formatCurrency(shortfall)}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Auction Performance Section */}
            <div className="auction-performance-section">
              <h2 className="section-title">Auction Performance</h2>
              <div className="performance-metrics">
                <div className="metric-card metric-total-bids">
                  <div className="metric-label">Total Bids</div>
                  <div className="metric-value">{allBids.length}</div>
                </div>
                <div className="metric-card metric-reserve-gap">
                  <div className="metric-label">Reserve Gap</div>
                  <div className="metric-value">{reserveGap}%</div>
                </div>
                <div className="metric-card metric-status">
                  <div className="metric-label">Status</div>
                  <div className="metric-value">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M12 9v4"></path>
                      <path d="M12 17h.01"></path>
                      <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"></path>
                    </svg>
                    Reserve Not Met
                  </div>
                </div>
              </div>
            </div>

            {/* Special Extension Offer Section */}
            <div className="extension-offer-section">
              <div className="offer-banner">
                <div className="offer-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                  </svg>
                </div>
                <div className="offer-content">
                  <h3>Special Extension Offer - 50% OFF!</h3>
                  <p>We understand your reserve wasn't met. Extend your auction at half the original price to give more buyers a chance to bid!</p>
                </div>
              </div>
              <div className="pricing-details">
                <div className="metric-card metric-original">
                  <div className="metric-label">Original Auction Fee</div>
                  <div className="metric-value original-price">₹1199</div>
                </div>
                <div className="metric-card metric-extension">
                  <div className="metric-label">Extension Fee (50% OFF)</div>
                  <div className="metric-value">{formatCurrency(600)}</div>
                </div>
                <div className="metric-card metric-savings">
                  <div className="metric-label">You Save</div>
                  <div className="metric-value">{formatCurrency(599)}</div>
                </div>
              </div>
            </div>

            {/* Select Extension Duration Section */}
            <div className="extension-duration-section">
              <h2 className="section-title">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                Select Extension Duration
              </h2>
              <div className="duration-options">
                <button 
                  className={`duration-option ${selectedExtensionDays === 3 ? 'active' : ''}`}
                  onClick={() => setSelectedExtensionDays(3)}
                >
                  3 days
                </button>
                <button 
                  className={`duration-option ${selectedExtensionDays === 7 ? 'active' : ''}`}
                  onClick={() => setSelectedExtensionDays(7)}
                >
                  7 days
                </button>
                <button 
                  className={`duration-option ${selectedExtensionDays === 14 ? 'active' : ''}`}
                  onClick={() => setSelectedExtensionDays(14)}
                >
                  14 days
                </button>
                <button 
                  className={`duration-option ${selectedExtensionDays === 30 ? 'active' : ''}`}
                  onClick={() => setSelectedExtensionDays(30)}
                >
                  30 days
                </button>
              </div>
              <div className="new-end-date">
                New End Date: {new Date(new Date().getTime() + selectedExtensionDays * 24 * 60 * 60 * 1000).toLocaleDateString("en-IN", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric"
                })}
              </div>
            </div>

            {/* Adjust Reserve Price Section */}
            <div className="adjust-reserve-section">
              <h2 className="section-title">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                Adjust Reserve Price (Optional)
              </h2>
              {!isEditingReservePrice ? (
                <div className="reserve-price-edit">
                  <div className="current-price">
                    <span>Current Reserve Price: {formatCurrency(reservePrice)}</span>
                  </div>
                  <button className="btn-edit-price" onClick={handleEditReservePrice}>Edit Price</button>
                </div>
              ) : (
                <div className="reserve-price-edit-form">
                  <div className="form-group">
                    <label htmlFor="new-reserve-price">New Reserve Price</label>
                    <input
                      type="number"
                      id="new-reserve-price"
                      className="reserve-price-input"
                      value={newReservePrice}
                      onChange={(e) => setNewReservePrice(e.target.value)}
                      placeholder="Enter new reserve price"
                      min="0"
                      step="1"
                    />
                  </div>
                  <div className="form-actions">
                    <button className="btn-cancel" onClick={handleCancelReservePriceEdit}>Cancel</button>
                    <button className="btn-save" onClick={handleSaveReservePrice}>Save Changes</button>
                  </div>
                  <div className="info-banner">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="16" x2="12" y2="12"></line>
                      <line x1="12" y1="8" x2="12.01" y2="8"></line>
                    </svg>
                    <span>Consider setting a more realistic reserve based on current market interest</span>
                  </div>
                </div>
              )}
            </div>

            {/* Why Extend Your Auction Section */}
            <div className="why-extend-section">
              <h2 className="section-title">Why Extend Your Auction?</h2>
              <div className="benefits-list">
                <div className="benefit-item">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6 9 17l-5-5"></path>
                  </svg>
                  <div>
                    <strong>More Exposure:</strong> Reach additional buyers who may have missed the first auction
                  </div>
                </div>
                <div className="benefit-item">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6 9 17l-5-5"></path>
                  </svg>
                  <div>
                    <strong>Market Timing:</strong> Sometimes buyers need more time to arrange financing
                  </div>
                </div>
                <div className="benefit-item">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6 9 17l-5-5"></path>
                  </svg>
                  <div>
                    <strong>50% Discount:</strong> Pay only half the original auction listing fee
                  </div>
                </div>
                <div className="benefit-item">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6 9 17l-5-5"></path>
                  </svg>
                  <div>
                    <strong>Keep Momentum:</strong> Existing bids remain, building on current interest
                  </div>
                </div>
              </div>
            </div>

            {/* Your Options Section */}
            <div className="your-options-section">
              <h2 className="section-title">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 9v4"></path>
                  <path d="M12 17h.01"></path>
                  <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"></path>
                </svg>
                Your Options:
              </h2>
              <div className="options-list">
                <div className="option-item">
                  <strong>1. Extend Auction:</strong> Take advantage of 50% discount and give your product more exposure
                </div>
                <div className="option-item">
                  <strong>2. End Auction:</strong> Close the auction and accept the highest bid (if acceptable)
                </div>
                <div className="option-item">
                  <strong>3. Cancel Auction:</strong> Remove the listing entirely
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="reserve-not-met-actions">
              <button className="btn-cancel-auction" onClick={() => setShowCancelConfirmModal(true)}>Cancel Auction</button>
              <button className="btn-end-current-bid" onClick={handleEndAuctionClick}>End with Current Bid</button>
              <button className="btn-extend-auction" onClick={() => setShowExtensionConfirmModal(true)}>Extend at 50% OFF</button>
            </div>
          </>
        ) : showDefaultBidsSection ? (
          <>
            {/* Default Header */}
            <div className="page-header">
              <div className="header-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              </div>
              <div className="header-content">
                <h1>Seller Reviews Bids</h1>
                <p>Seller evaluates received bids</p>
              </div>
            </div>

            {/* All Bids Section */}
            <div className="bids-section">
              <h2 className="section-title">{filterType === 'rejected' ? `Rejected Bids (${allBids.length})` : `Active Bids (${allBids.length})`}</h2>
              
              {allBids.length === 0 ? (
                <div className="no-bids">
                  <p>{filterType === 'rejected' ? 'No rejected bids for this auction' : 'No bids available for this auction'}</p>
                </div>
              ) : (
                <div className="bids-list">
                  {allBids.map((bid, index) => {
                    const isHighest = bid.is_highest_bid === 'Y' || bid.is_highest_bid === 'y' || index === 0;
                    const isAccepted = acceptedBid && bid.bid_id === acceptedBid.bid_id;
                    const isRejected = bid.bid_status === 'rejected' || filterType === 'rejected';
                    return (
                      <div
                        key={bid.bid_id}
                        className={`bid-card ${isHighest && !isAccepted && !isRejected ? 'highest-bid clickable' : ''} ${isAccepted ? 'accepted-bid' : ''} ${isRejected ? 'rejected-bid' : ''}`}
                        onClick={isHighest && !isAccepted && !isRejected ? () => navigate(`/auction/${auctionId}/winning-bid-review`) : undefined}
                      >
                        <div className="bid-left">
                          <div className="bidder-name">{bid.bidder_name || bid.bidder_company || "Unknown Bidder"}</div>
                          <div className="bid-time">{formatDate(bid.bid_placed_at || bid.created_at || bid.bid_date)}</div>
                        </div>
                        <div className="bid-right">
                          <div className="bid-amount">{formatCurrency(bid.bid_amount || bid.amount)}</div>
                          {isRejected && (
                            <div className="rejected-bid-label">Rejected</div>
                          )}
                          {!isRejected && isHighest && !isAccepted && (
                            <div className="highest-bid-label">Highest Bid</div>
                          )}
                          {!isRejected && isAccepted && (
                            <div className="accepted-bid-label">Accepted</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Information Footer */}
            {auction.end_date && (
              <div className="info-footer">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="clock-icon"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                <span>
                  Auction ends on {new Date(auction.end_date).toLocaleDateString("en-IN", {
                    year: "numeric",
                    month: "numeric",
                    day: "numeric",
                  })}. Seller can accept a bid at any time.
                </span>
              </div>
            )}
          </>
        ) : showWinnerDeclaredPendingAcceptance ? (
          <>
            <div className="auction-ended-header">
              <div className="header-content">
                <h1>Winner Declared</h1>
                <p>
                  A winning bid has been declared for this auction. Review and accept the bid to continue
                  with payment and fulfilment.
                </p>
              </div>
            </div>
            <div className="decision-section">
              <div className="decision-content">
                <div className="decision-buttons">
                  <button
                    className="btn-end-auction"
                    onClick={() => navigate(`/auction/${auctionId}/winning-bid-review`)}
                  >
                    Review Winning Bid
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : null}
          </>
        )}

        {/* Cancel Auction Confirmation Modal */}
        {showCancelConfirmModal && (
          <div className="cancel-confirm-modal-overlay" onClick={() => setShowCancelConfirmModal(false)}>
            <div className="cancel-confirm-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="cancel-confirm-section">
                <div className="cancel-confirm-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                </div>
                <div className="cancel-confirm-content">
                  <h2>Cancel Auction</h2>
                  <p className="instruction-text">Are you sure you want to cancel this auction? This action cannot be undone.</p>
                  
                  <div className="cancel-warning">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                      <line x1="12" y1="9" x2="12" y2="13"></line>
                      <line x1="12" y1="17" x2="12.01" y2="17"></line>
                    </svg>
                    <div>
                      <strong>Warning:</strong> Cancelling this auction will:
                      <ul>
                        <li>Remove the auction from active listings</li>
                        <li>Notify all bidders about the cancellation</li>
                        <li>Prevent any further bids on this auction</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="cancel-confirm-actions">
                <button className="btn-go-back" onClick={() => setShowCancelConfirmModal(false)}>
                  Go Back
                </button>
                <button className="btn-confirm-cancel" onClick={handleCancelAuction}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                  Confirm Cancellation
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Extension Confirmation Modal */}
        {showExtensionConfirmModal && (
          <div className="extension-confirm-modal-overlay" onClick={() => setShowExtensionConfirmModal(false)}>
            <div className="extension-confirm-modal-content" onClick={(e) => e.stopPropagation()}>
              {/* Confirm Auction Extension Section */}
              <div className="extension-confirm-section">
                <div className="extension-confirm-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6 9 17l-5-5"></path>
                  </svg>
                </div>
                <div className="extension-confirm-content">
                  <h2>Confirm Auction Extension</h2>
                  <p className="instruction-text">Please review the extension details before confirming.</p>
                  
                  {/* Extension Summary */}
                  <div className="extension-summary">
                    <div className="summary-row">
                      <span className="summary-label">Extension Duration:</span>
                      <span className="summary-value">{selectedExtensionDays} days</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">New End Date:</span>
                      <span className="summary-value">
                        {new Date(new Date().getTime() + selectedExtensionDays * 24 * 60 * 60 * 1000).toLocaleDateString("en-US", {
                          month: "numeric",
                          day: "numeric",
                          year: "numeric"
                        })}
                      </span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">Original Fee:</span>
                      <span className="summary-value">{formatCurrency(1199)}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">Discount (50% OFF):</span>
                      <span className="summary-value discount">-{formatCurrency(599)}</span>
                    </div>
                    <div className="summary-row total">
                      <span className="summary-label">Total Extension Fee:</span>
                      <span className="summary-value">{formatCurrency(600)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* What happens next Section */}
              <div className="extension-what-happens-section">
                <h3>What happens next?</h3>
                <ul>
                  <li>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                    Payment of {formatCurrency(600)} will be processed
                  </li>
                  <li>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                    Auction will be reactivated immediately
                  </li>
                  <li>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                    New end date: {new Date(new Date().getTime() + selectedExtensionDays * 24 * 60 * 60 * 1000).toLocaleDateString("en-US", {
                      month: "numeric",
                      day: "numeric",
                      year: "numeric"
                    })}
                  </li>
                  <li>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                    Existing bids will remain valid
                  </li>
                  <li>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                    Auction will be promoted to new buyers
                  </li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="extension-confirm-actions">
                <button className="btn-go-back" onClick={() => setShowExtensionConfirmModal(false)}>
                  Go Back
                </button>
                <button className="btn-confirm-pay" onClick={handleConfirmExtension}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6 9 17l-5-5"></path>
                  </svg>
                  Confirm & Pay {formatCurrency(600)}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirm Auction Winner Modal */}
        {showConfirmModal && highestBid && (
          <div className="confirm-modal-overlay" onClick={() => setShowConfirmModal(false)}>
            <div className="confirm-modal-content" onClick={(e) => e.stopPropagation()}>
              {/* Confirm Auction Winner Section */}
              <div className="confirm-winner-section">
                <div className="confirm-winner-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
                    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path>
                    <path d="M4 22h16"></path>
                    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path>
                    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path>
                    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path>
                  </svg>
                </div>
                <div className="confirm-winner-content">
                  <h2>
                    {winnerApprovalApprovedReady
                      ? "Confirm Winner Declaration"
                      : "Confirm Auction Winner"}
                  </h2>
                  <p className="warning-text">
                    {winnerApprovalApprovedReady
                      ? "Approvers have completed the review. Confirm to officially declare the highest valid bidder as the winner and end this auction."
                      : "You are about to declare the winner and end this auction. If winner approval is configured, approvers must complete review before the winner is officially declared."}
                  </p>
                  
                  {/* Winning Bid Details */}
                  <div className="winning-bid-details">
                    <div className="detail-row">
                      <span className="detail-label">Winner:</span>
                      <span className="detail-value">{highestBid.bidder_name || highestBid.bidder_company || "Unknown Bidder"}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Winning Bid:</span>
                      <span className="detail-value winning-bid-amount">{formatCurrency(highestBidAmount)}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Bid Time:</span>
                      <span className="detail-value">{formatDate(highestBid.bid_placed_at || highestBid.created_at || highestBid.bid_date)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* What happens next Section */}
              <div className="what-happens-next-section">
                <h3>What happens next?</h3>
                <ul>
                  <li>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                    If winner approval is configured, approvers must complete review before you can confirm the final winner declaration
                  </li>
                  <li>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                    Contact details will be exchanged
                  </li>
                  <li>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                    Proforma invoice will be generated
                  </li>
                  <li>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                    Inspection process will begin (if applicable)
                  </li>
                  <li>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                    Payment and delivery can proceed
                  </li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="confirm-modal-actions">
                <button className="btn-cancel" onClick={() => setShowConfirmModal(false)}>
                  Cancel
                </button>
                <button
                  className="btn-confirm"
                  onClick={handleConfirmEndAuction}
                  disabled={declaringWinner}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6 9 17l-5-5"></path>
                  </svg>
                  Confirm & Declare Winner
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
